"use client";

import React from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { motion } from "framer-motion";
import logo from "@/public/global/bitbull-logo.svg";

import { PageMentoringV2Schema } from "@/sanity/pages/PageMentoringV2Template";

const fadeInUp = (delay?: number) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function HeroSection({ data, className }: { data: PageMentoringV2Schema; className?: string }) {
  return (
    <section
      className={cn("relative overflow-clip bg-[#0A1A1D] pt-[8rem] lg:pt-[12rem] pb-[6rem] lg:pb-[10rem]", className)}>
      <div className="absolute top-0 w-full border-b border-white/10">
        <div className="w-[min(220rem,100%)] mx-auto py-[1.5rem] justify-between flex items-center relative">
          <Image
            src={logo}
            alt=""
            className="mx-auto lg:mx-0"
          />

          {data?.heroCTA && (
            <div className="hidden lg:block">
              <CTACornerButton data={data.heroCTA} />
            </div>
          )}
        </div>
      </div>
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
        className="hidden top-0 lg:block"
        orientation="vertical"
        position="right"
        style="light"
      />

      <div className="container flex flex-col items-center mt-[4rem]">
        <div className="w-[min(186rem,100%)] flex flex-col lg:flex-row items-center gap-[4rem] lg:gap-[8rem]">
          {/* Left: Content */}
          <div className="w-full lg:w-[55%] flex flex-col items-start">
            {data?.heroChip && (
              <motion.div {...fadeInUp()}>
                <Chip
                  className="mb-[1.5rem]"
                  image={data.heroChip.icon}
                  text={data.heroChip.text}
                />
              </motion.div>
            )}

            {data?.heroTitle && (
              <motion.div
                {...fadeInUp(0.1)}
                className="richtext richtext-start lg:prose-h1:text-[10rem] prose-h1:text-[6rem] prose-h1:leading-[100%] prose-h1:font-ivy-headline-regular prose-h1:text-white prose-h1:tracking-[.125rem] prose-em:font-ivy prose-em:tracking-[0.3px] prose-em:font-light">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {data?.heroText && (
              <motion.div
                {...fadeInUp(0.15)}
                className="richtext richtext-start mt-[2rem] w-[min(66.375rem,100%)] prose-p:text-white/80">
                <PortableText
                  value={data.heroText}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            <div className="flex flex-col w-full gap-[1.5rem] lg:w-fit">
              {data?.heroCTA && (
                <motion.div
                  className="w-full sm:w-auto"
                  {...fadeInUp(0.2)}>
                  <CTACornerButton
                    className="mt-[4rem]"
                    data={data.heroCTA}
                  />
                </motion.div>
              )}

              {data?.heroCallout && (
                <motion.div
                  className="flex justify-center items-center w-full bg-[#FFAC2F]/10 py-[.5rem] gap-[1rem]"
                  {...fadeInUp(0.25)}>
                  <div className="flex items-center gap-[.5rem]">
                    <div className="bg-[#FFAC2F] w-[1rem] h-[1rem] rounded-[.25rem]" />
                    <div className="bg-[#FFAC2F] w-[1rem] h-[1rem] rounded-[.25rem]" />
                    <div className="bg-[#FFAC2F] w-[1rem] h-[1rem] rounded-[.25rem]" />
                  </div>
                  <p className="m-0 uppercase text-[1.75rem] text-[#FFAC2F] font-geistmono">{data.heroCallout}</p>
                </motion.div>
              )}
            </div>
          </div>

          {/* Right: Image */}
          {data?.heroImage && (
            <motion.div
              {...fadeInUp(0.15)}
              className="w-full lg:w-[45%] flex-shrink-0 relative">
              <div className="flex absolute flex-col bottom-[3rem] left-[3rem] z-[5] gap-[2rem]">
                {data?.heroAssets.map((item, index) => (
                  <div
                    className="flex items-center bg-white"
                    key={index}>
                    <div className="flex justify-center items-center h-full bg-[#D9F9B9] w-[6rem] py-[1rem]">
                      <p className="m-0 text-black text-[2rem] font-geist">{item.value}</p>
                    </div>
                    <div className="p-[1rem]">
                      <p className="m-0 text-black">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Image
                className="object-cover relative w-full h-auto z-[1]"
                src={data.heroImage}
                alt=""
                width={data.heroImage.width}
                height={data.heroImage.height}
                placeholder={data.heroImage.lqip ? "blur" : undefined}
                blurDataURL={data.heroImage.lqip}
              />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
